import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import type { OverlayAction } from "./OverlayPage";

interface OverlayActionBarProps {
  primaryAction: OverlayAction;
  secondaryAction?: OverlayAction;
  tertiaryAction?: OverlayAction;
  /** 
   * Danger action - renders as small text link, never competing with primary
   * Use for destructive actions like "Delete" that shouldn't have equal visual weight
   */
  dangerAction?: OverlayAction;
}

/**
 * OverlayActionBar - Standard Side Sheet footer (72dp height).
 * 
 * Canonical Standard Side Sheet Baseline:
 * - Height: 72dp total
 * - Padding: pt-4 pb-6 (16dp top, 24dp bottom)
 * - Horizontal padding: px-6 (24dp)
 * - Button alignment: PRIMARY LEFT (Primary first, Secondary after)
 * - Buttons: Normal size, NOT full-width on desktop
 * 
 * Mobile: Primary full-width on top, secondary/tertiary below
 * Desktop: Primary LEFT, Secondary next, Tertiary after (all left-aligned)
 */
export function OverlayActionBar({
  primaryAction,
  secondaryAction,
  tertiaryAction,
  dangerAction,
}: OverlayActionBarProps) {
  const isMobile = useIsMobile();

  return (
    <div className="sticky bottom-0 z-40 bg-muted/30 border-t border-border px-4 sm:px-6 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pt-4 sm:pb-6 shrink-0">
      {isMobile ? (
        // Mobile: Stack buttons vertically, primary on top
        <div className="flex flex-col gap-2">
          <Button
            onClick={() => primaryAction.onClick()}
            disabled={primaryAction.disabled || primaryAction.loading}
            variant={primaryAction.variant || "default"}
            className="w-full h-11"
          >
            {primaryAction.loading && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            {primaryAction.label}
          </Button>

          <div className="flex gap-2">
            {tertiaryAction && (
              <Button
                variant={tertiaryAction.variant || "outline"}
                onClick={() => tertiaryAction.onClick()}
                disabled={tertiaryAction.disabled || tertiaryAction.loading}
                className="flex-1 h-10"
              >
                {tertiaryAction.loading && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {tertiaryAction.label}
              </Button>
            )}

            {secondaryAction && (
              <Button
                variant="ghost"
                onClick={() => secondaryAction.onClick()}
                disabled={secondaryAction.disabled || secondaryAction.loading}
                className="flex-1 h-10 text-muted-foreground"
              >
                {secondaryAction.loading && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {secondaryAction.label}
              </Button>
            )}
          </div>

          {/* Danger action - small text link at bottom on mobile */}
          {dangerAction && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dangerAction.onClick()}
              disabled={dangerAction.disabled || dangerAction.loading}
              className="text-destructive hover:text-destructive hover:bg-destructive/10 text-xs h-8 mt-1"
            >
              {dangerAction.loading && (
                <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
              )}
              {dangerAction.label}
            </Button>
          )}
        </div>
      ) : (
        // Desktop: PRIMARY LEFT - Primary first, then Secondary, then Tertiary
        // Danger action floats to the right as small link
        <div className="flex items-center justify-between gap-3">
          {/* Left side: Primary + Secondary + Tertiary (all left-aligned) */}
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              onClick={() => primaryAction.onClick()}
              disabled={primaryAction.disabled || primaryAction.loading}
              variant={primaryAction.variant || "default"}
            >
              {primaryAction.loading && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {primaryAction.label}
            </Button>

            {secondaryAction && (
              <Button
                variant="ghost"
                onClick={() => secondaryAction.onClick()}
                disabled={secondaryAction.disabled || secondaryAction.loading}
                className="text-muted-foreground hover:text-foreground"
              >
                {secondaryAction.loading && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {secondaryAction.label}
              </Button>
            )}

            {tertiaryAction && (
              <Button
                variant={tertiaryAction.variant || "outline"}
                onClick={() => tertiaryAction.onClick()}
                disabled={tertiaryAction.disabled || tertiaryAction.loading}
              >
                {tertiaryAction.loading && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {tertiaryAction.label}
              </Button>
            )}
          </div>

          {/* Right side: Danger action only (small link) */}
          {dangerAction && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => dangerAction.onClick()}
              disabled={dangerAction.disabled || dangerAction.loading}
              className="text-destructive hover:text-destructive hover:bg-destructive/10 text-xs h-8 px-2"
            >
              {dangerAction.loading && (
                <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
              )}
              {dangerAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
